import React from 'react'
import {
  Alert,
  Button,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Tab,
  Tabs,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import type { SelectChangeEvent } from '@mui/material/Select'
import { usePageTitle } from '../../components/common/PageTitleContext'
import {
  getSuperadminSubscriptionOverview,
  getSuperadminSubscriptionSettings,
  saveSuperadminSubscriptionSettings,
  updateStationAdminRole,
  type CreditPolicySettings,
  type SubscriptionStation,
  type YoutubeCostSettings,
} from '../../api/superadminSubscriptions'

type NoticeState = {
  type: 'success' | 'error' | 'info'
  message: string
}

type AdminFormState = {
  stationId: string
  userId: string
  role: 'admin' | 'presenter'
}

const defaultPolicy: CreditPolicySettings = {
  included_monthly: 10,
  subscription_rollover_months: 2,
  pack_valid_months: 12,
}

const defaultCosts: YoutubeCostSettings = {
  audio: 1,
  video: 2,
}

const SubscriptionsConsolePage: React.FC = () => {
  usePageTitle('Subscriptions')

  const [overview, setOverview] = React.useState<SubscriptionStation[]>([])
  const [policy, setPolicy] = React.useState<CreditPolicySettings>(defaultPolicy)
  const [costs, setCosts] = React.useState<YoutubeCostSettings>(defaultCosts)
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [notice, setNotice] = React.useState<NoticeState | null>(null)
  const [activeTab, setActiveTab] = React.useState<'subscriptions' | 'admins' | 'credits'>('subscriptions')
  const [adminForm, setAdminForm] = React.useState<AdminFormState>({
    stationId: '',
    userId: '',
    role: 'admin',
  })

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setNotice(null)

    try {
      const [overviewRes, settingsRes] = await Promise.all([
        getSuperadminSubscriptionOverview(),
        getSuperadminSubscriptionSettings(),
      ])
      setOverview(overviewRes.stations ?? [])
      setPolicy(settingsRes.credit_policy ?? defaultPolicy)
      setCosts(settingsRes.youtube_costs ?? defaultCosts)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load subscription data.'
      setNotice({ type: 'error', message })
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  const handleSaveSettings = async () => {
    setSaving(true)
    setNotice(null)

    try {
      const response = await saveSuperadminSubscriptionSettings({
        credit_policy: policy,
        youtube_costs: costs,
      })
      setPolicy(response.credit_policy ?? policy)
      setCosts(response.youtube_costs ?? costs)
      setNotice({ type: 'success', message: 'Subscription settings saved.' })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save subscription settings.'
      setNotice({ type: 'error', message })
    } finally {
      setSaving(false)
    }
  }

  const handleAdminSubmit = async () => {
    const stationId = Number.parseInt(adminForm.stationId, 10)
    const userId = Number.parseInt(adminForm.userId, 10)

    if (!stationId || !userId) {
      setNotice({ type: 'error', message: 'Station ID and user ID are required.' })
      return
    }

    setSaving(true)
    setNotice(null)

    try {
      await updateStationAdminRole({
        station_id: stationId,
        user_id: userId,
        role: adminForm.role,
      })
      setNotice({ type: 'success', message: 'Station admin role updated.' })
      await loadData()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update station admin role.'
      setNotice({ type: 'error', message })
    } finally {
      setSaving(false)
    }
  }

  const handleRoleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value === 'presenter' ? 'presenter' : 'admin'
    setAdminForm((prev) => ({ ...prev, role: value }))
  }

  const handleTabChange = (_event: React.SyntheticEvent, value: string) => {
    if (value === 'admins' || value === 'credits') {
      setActiveTab(value)
      return
    }

    setActiveTab('subscriptions')
  }

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="h4" component="h1">
          Subscriptions
        </Typography>
        <Typography color="text.secondary">
          Review station subscriptions, manage admins, and update credit policy settings.
        </Typography>
      </Stack>

      {notice ? <Alert severity={notice.type}>{notice.message}</Alert> : null}

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Subscription console tabs"
          sx={{ px: 2 }}
        >
          <Tab value="subscriptions" label="Subscriptions" />
          <Tab value="admins" label="Manage station admins" />
          <Tab value="credits" label="YT credit policy & costs" />
        </Tabs>
      </Paper>

      {activeTab === 'subscriptions' ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Station overview</Typography>
            {loading ? (
              <Typography color="text.secondary">Loading subscription data...</Typography>
            ) : overview.length === 0 ? (
              <Typography color="text.secondary">No stations found.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Station</TableCell>
                    <TableCell>Subscription</TableCell>
                    <TableCell>Seats</TableCell>
                    <TableCell>Admins</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overview.map((station) => {
                    const status = station.subscription?.status ?? 'none'
                    const seatLabel = station.seats
                      ? `Inbox ${station.seats.inbox.assigned}/${station.seats.inbox.total} | Prep ${station.seats.prep.assigned}/${station.seats.prep.total}`
                      : 'N/A'
                    return (
                      <TableRow key={station.id}>
                        <TableCell>
                          <Typography>{station.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {station.website_domain || station.website_url || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={status}
                            color={status === 'active' ? 'success' : status === 'trialing' ? 'info' : 'default'}
                            variant={status === 'active' || status === 'trialing' ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>{seatLabel}</TableCell>
                        <TableCell>
                          {station.admins.length === 0 ? (
                            <Typography color="text.secondary">No admins</Typography>
                          ) : (
                            station.admins.map((admin) => (
                              <Typography key={admin.user_id} variant="body2">
                                {admin.display_name} ({admin.email})
                              </Typography>
                            ))
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </Stack>
        </Paper>
      ) : null}

      {activeTab === 'credits' ? (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6">Credit policy</Typography>
              <Typography color="text.secondary">
                Configure included credits and rollover windows used for new credit grants.
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Included credits per month"
                  value={policy.included_monthly}
                  onChange={(event) =>
                    setPolicy((prev) => ({ ...prev, included_monthly: Number(event.target.value) }))
                  }
                  type="number"
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label="Subscription rollover months"
                  value={policy.subscription_rollover_months}
                  onChange={(event) =>
                    setPolicy((prev) => ({ ...prev, subscription_rollover_months: Number(event.target.value) }))
                  }
                  type="number"
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label="Pack validity (months)"
                  value={policy.pack_valid_months}
                  onChange={(event) =>
                    setPolicy((prev) => ({ ...prev, pack_valid_months: Number(event.target.value) }))
                  }
                  type="number"
                  inputProps={{ min: 1 }}
                />
              </Stack>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6">YouTube credit costs</Typography>
              <Typography color="text.secondary">
                Set credit costs for audio and video downloads.
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Audio cost"
                  value={costs.audio}
                  onChange={(event) => setCosts((prev) => ({ ...prev, audio: Number(event.target.value) }))}
                  type="number"
                  inputProps={{ min: 1 }}
                />
                <TextField
                  label="Video cost"
                  value={costs.video}
                  onChange={(event) => setCosts((prev) => ({ ...prev, video: Number(event.target.value) }))}
                  type="number"
                  inputProps={{ min: 1 }}
                />
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="flex-end">
                <Button variant="contained" onClick={handleSaveSettings} disabled={saving}>
                  {saving ? 'Saving...' : 'Save settings'}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      ) : null}

      {activeTab === 'admins' ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">Manage station admins</Typography>
            <Typography color="text.secondary">
              Promote an existing station member to admin or revert them to presenter.
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <FormControl sx={{ minWidth: { xs: '100%', md: 220 } }}>
                <FormLabel>Station</FormLabel>
                <Select
                  value={adminForm.stationId}
                  onChange={(event) =>
                    setAdminForm((prev) => ({ ...prev, stationId: event.target.value }))
                  }
                  size="small"
                  displayEmpty
                >
                  <MenuItem value="">Select station</MenuItem>
                  {overview.map((station) => (
                    <MenuItem key={station.id} value={station.id}>
                      {station.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="User ID"
                value={adminForm.userId}
                onChange={(event) =>
                  setAdminForm((prev) => ({ ...prev, userId: event.target.value }))
                }
                type="number"
                inputProps={{ min: 1 }}
                size="small"
              />
              <FormControl sx={{ minWidth: { xs: '100%', md: 180 } }}>
                <FormLabel>Role</FormLabel>
                <Select value={adminForm.role} onChange={handleRoleChange} size="small">
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="presenter">Presenter</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={handleAdminSubmit} disabled={saving}>
                Update role
              </Button>
            </Stack>
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  )
}

export default SubscriptionsConsolePage
